import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import { cn } from "@client/utils/cn";
import { Text } from "@sfc/ui";

export const Page = withErrorBoundary(
  ({
    className,
    containerClassName,
    title,
    description,
    cta,
    breadcrumbs,
    spacing = "default",
    children,
    scrollerProps,
  }: {
    className?: string;
    containerClassName?: string;
    title?: string;
    description?: string;
    cta?: React.ReactNode;
    breadcrumbs?: React.ReactNode;
    /** Header-led pages keep top spacing in PageHeader, including while sticky. */
    spacing?: "default" | "compact" | "header";
    scrollerProps?: React.ComponentProps<typeof RouteScroller> & {
      [key: `data-${string}`]: string | boolean;
    };
    children?: React.ReactNode;
  }) => {
    // Let RouteScroller own overflow for header-led pages; an inner overflow container traps sticky headers.
    const Container = spacing === "header" ? "main" : "div";

    return (
      <RouteScroller {...scrollerProps}>
        <Container
          className={cn(
            "relative flex-1 mx-auto w-full max-w-4xl px-4 md:px-8 flex flex-col gap-4 md:gap-6",
            spacing === "header"
              ? "pb-6 md:pb-8"
              : [
                  "overflow-x-hidden",
                  spacing === "compact" ? "py-6 md:py-8" : "py-12 md:py-24",
                ],
            containerClassName,
          )}
        >
          {breadcrumbs}
          {(title || cta) && (
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="min-w-0">
                <h1
                  data-slot="page-title"
                  className="text-2xl font-bold [overflow-wrap:anywhere]"
                >
                  {title}
                </h1>
                {description && (
                  <Text size="xs" tone="muted-foreground">
                    {description}
                  </Text>
                )}
              </div>
              {cta}
            </div>
          )}
          <div className={cn("flex-1", className)}>{children}</div>
        </Container>
      </RouteScroller>
    );
  },
);
